import "./index.scss";
import React, {ReactNode} from "react";
import DocumentBar from "./menu/DocumentBar";
import StartScreen from "./startscreen/StartScreen";
import ViewerScreen from "./viewer/ViewerScreen";
import PortalMenuBar from "./menu/PortalMenuBar";
import ContentMode from "./ContentMode";
import portalConfiguration from "../shared/configuration/PortalConfiguration";
import i18next from "i18next";
import PortalDialogs, {PortalDialogsGlobals} from "./dialog/PortalDialogs";
import {handleTextLayerSelectionChange, initTextLayerGlobals, TextLayerGlobals} from "./viewer/pdf/TextLayer";
import {initTextSearchGlobals, TextSearchGlobals} from "./document/components/pdf/TextSearch";
import {DocumentFilterGlobals, initDocumentFilterGlobals} from "./menu/DocumentFilterComponent";
import {initPortalDocumentManagerGlobals, PortalDocumentManagerGlobals} from "./document/PortalDocumentManager";
import {PortalDocumentInterface} from "./document/PortalDocument";
import {DropzoneGlobals, initDropzoneGlobals} from "./startscreen/Dropzone";
import LoadingOverlay from "../shared/session/components/LoadingOverlay";
import PortalContext from "./PortalContext";
import AbstractPage from "../shared/AbstractPage";
import PortalMessageLog from "./log/PortalMessageLog";
import Immutable from "../shared/immutable/Immutable";
import {DocumentSortingGlobals, initDocumentSortingGlobals} from "./document/DocumentSorting";
import {
	Information,
	UserCertificates,
	UserInterfaceResources
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {DocumentListGlobals, initDocumentListGlobals} from "./startscreen/DocumentList";
import {initSidebarFunctionGlobals, SidebarFunctionGlobals} from "./document/components/pdf/SidebarFunctions";
import UserSettings from "./session/UserSettings";
import {DebugGlobals, initDebugGlobals} from "./dialog/components/settings/DebugDialog";
import HelpOverlay, {HelpOverlayGlobals} from "../shared/components/HelpOverlay";
import LengthUnit from "../shared/units/LengthUnit";
import StorageItem from "../shared/StorageItem";
import ReconnectDialog from "../shared/dialog/components/ReconnectDialog";
import FunctionHelper from "../shared/helper/FunctionHelper";
import {AreaLayerGlobals, initAreaLayerGlobals} from "./viewer/pdf/AreaLayerInterface";
import {AbstractPageState} from "../shared/AbstractPageInterface";
import {isDesktop, isMobile} from "react-device-detect";
import {FormLayerGlobals, initFormLayerGlobals} from "./viewer/pdf/form/FormLayerInterface";
import {PortalDragEvent} from "../shared/interface/PortalDragEvent";
import {Tooltip} from "primereact/tooltip";

export interface PortalGlobals {
	context: PortalContext;
	contentMode: ContentMode;
	userCertificates: Immutable<UserCertificates>;
	userSettings: Immutable<UserSettings>;
	dragEvent?: Immutable<PortalDragEvent>;
}

export function initPortalGlobals(): PortalGlobals {
	return {
		context: new PortalContext(),
		contentMode: ContentMode.STARTSCREEN,
		userCertificates: Immutable.from(new UserCertificates({})),
		userSettings: Immutable.from(new UserSettings())
	};
}

export interface PortalState extends AbstractPageState, PortalDialogsGlobals, AreaLayerGlobals, PortalGlobals,
	TextLayerGlobals, DocumentFilterGlobals, DocumentSortingGlobals, TextSearchGlobals, PortalDocumentManagerGlobals,
	DropzoneGlobals, DocumentListGlobals, SidebarFunctionGlobals, DebugGlobals, HelpOverlayGlobals, FormLayerGlobals {
	loading: boolean;
}

/**
 * The portal component is the root component of all other components
 */
export default class Portal extends AbstractPage<any, PortalState> {
	public static route: string = "";

	/**
	 * Page component constructor
	 */
	constructor(props: Readonly<any>) {
		super(props);

		this.state = {
			...this.state,
			...initPortalGlobals(),
			...initDocumentListGlobals(),
			...initTextSearchGlobals(),
			...initDocumentSortingGlobals(),
			...initDocumentFilterGlobals(),
			...initDropzoneGlobals(),
			...initAreaLayerGlobals(),
			...initTextLayerGlobals(),
			...initPortalDocumentManagerGlobals(),
			...initDebugGlobals(),
			...initSidebarFunctionGlobals(),
			...initFormLayerGlobals(),
			loading: true
		};

		this.addMessage = this.addMessage.bind(this);
		this.setState = this.setState.bind(this);
		this.handleClick = this.handleClick.bind(this);
		this.handleKeyUp = this.handleKeyUp.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleMouseUp = this.handleMouseUp.bind(this);
		this.handleWheel = this.handleWheel.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		// reset in portal to use normal language detection
		portalConfiguration.fixedAdminLanguage = false;

		let context: PortalContext = this.state.context;
		await context.initialize(this);

		let unit: LengthUnit | string | null = localStorage.getItem(StorageItem.UNIT);
		if (unit === null) {
			switch (i18next.language) {
				case "de":
					unit = LengthUnit.MILLIMETER;
					break;
				case "en":
					unit = LengthUnit.INCHES;
					break;
			}
		}

		let userSettings: UserSettings = UserSettings.fromJson(this.state.userSettings.get());
		userSettings.unit = unit as LengthUnit;
		this.state.context.session.updateUserSettings(userSettings);

		window.addEventListener('mouseup', this.handleMouseUp);
		window.addEventListener('keyup', this.handleKeyUp);
		window.addEventListener('keydown', this.handleKeyDown);
		window.addEventListener('wheel', this.handleWheel, {passive: false});

		if (isMobile) {
			window.document.body.classList.add("mobile-device");
		} else if (isDesktop) {
			window.document.body.classList.add("desktop-device");
		}

		this.setState({
			loading: false
		});
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('mouseup', this.handleMouseUp);
		window.removeEventListener('keyup', this.handleKeyUp);
		window.removeEventListener('keydown', this.handleKeyDown);
		window.removeEventListener('wheel', this.handleWheel);
	}

	private handleMouseUp() {
		handleTextLayerSelectionChange(this);
	}

	private handleKeyUp() {
		handleTextLayerSelectionChange(this);
	}

	private handleKeyDown(event: KeyboardEvent) {
		// Prevent browser zoom
		if (event.ctrlKey && (event.key === "+" || event.key === "-")) {
			event.preventDefault();
		}
	}

	private handleWheel(event: WheelEvent) {
		// Prevent browser zoom
		if (event.ctrlKey) {
			event.preventDefault();
		}
	}

	private handleClick(): void {
		this.state.context.session.resetIdle();
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!this.state.context.initialized || this.state.loading) {
			return (
				<>
					{FunctionHelper.applyValue(this.state.reconnectDialogTries, 0) >= 1 ?
						<ReconnectDialog {...this.state} visible={true} language={i18next.language}
										 tries={this.state.reconnectDialogTries}
										 hide={() => this.props.context.getPage().setState({
											 showReconnectDialog: false
										 })}/> :
						<LoadingOverlay visible={true} language={i18next.language}/>
					}
					<div className="page" ref={this.pageElement}>
						<div className="message-panel">{this.renderMessages()}</div>
					</div>
				</>
			)
		}

		let selection: PortalDocumentInterface | undefined;
		if (this.state.documentSelection.get().length === 1) {
			selection = this.state.documentSelection.get().pop();
		}

		let portalInfo: Information = this.state.context.info;
		let logo: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.logoFileName, UserInterfaceResources.getLogoFileNameDefault()
		);
		if (FunctionHelper.isEmpty(logo)) {
			logo = "logo" + portalConfiguration.cachingPath + ".png";
		}

		let theme: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.themeFileName, UserInterfaceResources.getThemeFileNameDefault()
		);
		if (FunctionHelper.isEmpty(theme)) {
			theme = "theme" + portalConfiguration.cachingPath + ".css";
		}

		let background: string = FunctionHelper.applyValue(
			portalInfo.userInterface?.resources?.backgroundFileName, UserInterfaceResources.getBackgroundFileNameDefault()
		);
		if (FunctionHelper.isEmpty(background)) {
			background = "background" + portalConfiguration.cachingPath + ".png";
		}

		let serverVersion: string = FunctionHelper.applyValue(
			portalInfo.serverVersion, Information.getServerVersionDefault()
		).replace(/ *\([^)]*\) */g, "").trim();
		let serverVersionParts: Array<string> = serverVersion.split(" ");

		return (
			<>
				<div className="page" ref={this.pageElement} hidden={!this.state.context.initialized}
					 onDrop={(event) => event.preventDefault()}
					 onDragOver={(event) => event.preventDefault()}
					 onClick={() => this.handleClick()}>
					<div className="header">
						<div className="header-menu">
							<div className="logo">
								<img src={portalConfiguration.baseUrl + "image/res/" + logo} alt="webPDF Logo"
									 onClick={() => window.location.reload()}/>
								<p className="version">
									<span>{serverVersionParts[0]} {serverVersionParts[1]}</span>
									{serverVersionParts.length > 2 ? <><br/><span>{serverVersionParts[2]}</span></> : <></>}
								</p>
							</div>
							<PortalMenuBar {...this.state} language={i18next.language}
										   userId={this.state.context.user?.uniqueId}/>
						</div>
						<DocumentBar {...this.state} language={i18next.language}/>
					</div>
					<div className="content">
						{this.state.contentMode === ContentMode.STARTSCREEN ?
							<StartScreen {...this.state} language={i18next.language}/> :
							<ViewerScreen {...this.state} portalDocument={Immutable.from(selection!)}
										  language={i18next.language}/>
						}
						<PortalDialogs {...this.state} language={i18next.language}/>
					</div>
					<div className="message-panel">{this.renderMessages()}</div>
					<PortalMessageLog {...this.state} resizeContainer={this.pageElement} language={i18next.language}/>
					<HelpOverlay {...this.state} language={i18next.language}/>
				</div>
				<img className="background-image" src={portalConfiguration.baseUrl + "image/res/" + background}
					 alt="webPDF Background"/>
				<link href={portalConfiguration.baseUrl + "css/res/" + theme} rel="stylesheet" type="text/css"/>
				<Tooltip target=".tooltip" style={{maxWidth: "300px", whiteSpace: "normal"}}
						 appendTo={document.getElementById("root")}/>
			</>
		);
	}
}